import HomeWorkspace from './HomeWorkspace.jsx';
import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Search,
  Menu,
  X,
  Globe,
  Calculator,
  Workflow,
  Shield,
  Code,
  ChartNoAxesCombined,
  FileText,
  Star,
  Check,
  Layers,
  PackageSearch,
  CheckCircle2,
  AlertCircle,
  ThumbsUp,
  HelpCircle,
  Zap,
  Sparkles,
} from "lucide-react";
import { getToolDetails } from '../shared/tool-details.mjs';
import { ToolVisualCard } from './ToolVisualCard.jsx';
import { AuthModal } from './AuthModal.jsx';
import { ConsultationForm } from './ConsultationForm.jsx';
import {
  tools,
  categories,
  featuredSlugs,
  relatedTools,
} from "../shared/tool-registry.mjs";
import { ToolWorkspace } from "./Tools.jsx";
import { NetworkTool } from "./ExtraTools.jsx";
import PublicSearchContent from './PublicSearchContent.jsx';
import {Extra} from './route-components.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import {toolGuides} from '../shared/tool-guides.mjs';
import {searchToolGuides} from '../shared/search-tool-guides.mjs';
import SeoAuditGuide from './SeoAuditGuide.jsx';
import { WorldBusinessClock } from './WorldBusinessClock.jsx';
import {shopCategories,shopCategoryLabel,shopSearchLink} from '../shared/shop-catalog.mjs';
const icons = {
  website: Globe,
  seo: Search,
  marketing: ChartNoAxesCombined,
  business: FileText,
  domain: Globe,
  security: Shield,
  calculators: Calculator,
  developer: Code,
  ai: Workflow,
  text: FileText,
  images: Layers,
  pdf: FileText,
};
const menuGroups = [
  ["Free Assets", "downloads"],
  ["AI Tools", "ai"],
  ["Free Tools", "all"],
  ["Shop", "shop"],
  ["Website Tools", "website"],
  ["Marketing", "marketing"],
  ["Automation", "automation"],
  ["Calculators", "calculators"],
  ["Resources", "resources"],
];
const groupCategories = {
  downloads: [],
  ai: ["ai"],
  all: categories.map((c) => c[0]),
  website: ["website", "seo", "domain", "security"],
  marketing: ["marketing", "business"],
  automation: ["ai", "developer"],
  calculators: ["calculators"],
  shop: [],
  resources: [],
};
export function PlatformHeader({ settings, Brand }) {
  const [open, setOpen] = useState(null),
    [mobile, setMobile] = useState(false);
  const [member, setMember] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const root = useRef(null),
    toggle = useRef(null),
    userMenuRef = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('bizaipro_member');
      if (saved) setMember(JSON.parse(saved));
    } catch {}

    fetch('/api/member/session')
      .then((r) => r.json())
      .then((d) => {
        if (d?.member) {
          setMember(d.member);
          try { localStorage.setItem('bizaipro_member', JSON.stringify(d.member)); } catch {}
        }
      })
      .catch(() => {});

    const onAuthChange = (e) => {
      setMember(e.detail);
    };
    window.addEventListener('bizaipro-auth-change', onAuthChange);
    return () => window.removeEventListener('bizaipro-auth-change', onAuthChange);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch('/api/member/logout', { method: 'POST' });
    } catch {}
    try { localStorage.removeItem('bizaipro_member'); } catch {}
    setMember(null);
    setUserDropdownOpen(false);
    window.dispatchEvent(new CustomEvent('bizaipro-auth-change', { detail: null }));
  };

  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (mobile) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    };
  }, [mobile]);

  useEffect(() => {
    const close = (e) => {
      if (e.key === "Escape") {
        setOpen(null);
        setMobile(false);
        setUserDropdownOpen(false);
        toggle.current?.focus();
      }
      if (e.type === "pointerdown" && !root.current?.contains(e.target)) {
        setOpen(null);
        setMobile(false);
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", close);
    document.addEventListener("pointerdown", close);
    return () => {
      document.removeEventListener("keydown", close);
      document.removeEventListener("pointerdown", close);
    };
  }, []);
  const selected = menuGroups.find((g) => g[1] === open),
    groups = groupCategories[open] || [];
  return (
    <header className="platform-header" ref={root}>
      <WorldBusinessClock />
      <div className="platform-header-inner">
        <Brand settings={settings} />
        <nav
          className={"platform-nav" + (mobile ? " mobile-open" : "")}
          aria-label="Main navigation"
        >
          {menuGroups.map(([label, key]) => (
            <button
              type="button"
              key={key}
              aria-expanded={open === key}
              aria-controls="platform-menu"
              onClick={() => setOpen(open === key ? null : key)}
            >
              {label}
              <ChevronDown size={13} />
            </button>
          ))}
          <a href="/learn/">Learn</a>
          <a href="/markets/">Markets</a>
          <a href="/blog/">Blog</a>
          {mobile && !member && (
            <button
              type="button"
              className="button full"
              style={{ marginTop: "14px", minHeight: "44px" }}
              onClick={() => {
                setMobile(false);
                setAuthModalOpen(true);
              }}
            >
              Sign in with Google
            </button>
          )}
        </nav>
        <div className="platform-header-actions">
          <ThemeToggle />
          <a href="/search/" title="Search" aria-label="Search">
            <Search size={20} />
          </a>
          {member ? (
            <div className="header-member-wrap" ref={userMenuRef}>
              <button
                type="button"
                className="header-member-avatar-btn"
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                aria-label={`Account menu for ${member.name || member.email}`}
              >
                <span className="avatar-initial">
                  {(member.name || member.email || 'M').charAt(0).toUpperCase()}
                </span>
              </button>
              {userDropdownOpen && (
                <div className="header-member-dropdown">
                  <div className="dropdown-user-header">
                    <strong>{member.name || 'BizAIPro Member'}</strong>
                    <small>{member.email}</small>
                  </div>
                  <hr />
                  <a href="/account/" className="dropdown-user-item" onClick={() => setUserDropdownOpen(false)}>
                    <span>My Account & Orders</span>
                    <span className="dropdown-counter">Licenses</span>
                  </a>
                  <a href="/markets/" className="dropdown-user-item" onClick={() => setUserDropdownOpen(false)}>
                    <span>Market Watchlist</span>
                    <span className="dropdown-counter">Live</span>
                  </a>
                  <a href="/downloads/" className="dropdown-user-item" onClick={() => setUserDropdownOpen(false)}>
                    <span>Free Assets Vault</span>
                    <span className="dropdown-counter">500+</span>
                  </a>
                  <button type="button" className="dropdown-logout-action" onClick={handleLogout}>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="header-signin-btn"
              onClick={() => setAuthModalOpen(true)}
              aria-label="Continue with Google"
            >
              <svg width="15" height="15" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.66v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.15z"/>
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.26v3.15C3.27 21.36 7.35 24 12 24z"/>
                <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.26C.46 8.17 0 9.99 0 12s.46 3.83 1.26 5.42l4.02-3.15z"/>
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.35 0 3.27 2.64 1.26 6.58l4.02 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
              </svg>
              <span>Sign in</span>
            </button>
          )}
          <a className="button small header-explore-btn" href="/free-tools/">
            Explore tools
            <ArrowUpRight size={15} />
          </a>
          <button
            ref={toggle}
            type="button"
            className="platform-mobile-toggle icon-button"
            title={mobile ? "Close navigation" : "Open navigation"}
            aria-label={mobile ? "Close navigation" : "Open navigation"}
            aria-expanded={mobile}
            onClick={() => {
              setMobile(!mobile);
              setOpen(null);
            }}
          >
            {mobile ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {mobile && (
        <div
          className="platform-mobile-backdrop"
          onClick={() => {
            setMobile(false);
            setOpen(null);
          }}
          aria-hidden="true"
        />
      )}
      {open && (
        <div id="platform-menu" className="platform-menu">
          <div className="container mega-inner">
            <div className="mega-main">
              <div className="mega-heading">
                <div>
                  <h2>{selected?.[0]}</h2>
                  <p>
                    {open === "resources"
                      ? "Practical ideas for your next business decision."
                      : "Find a tool for the work in front of you."}
                  </p>
                </div>
                <button
                  className="icon-button"
                  aria-label="Close menu"
                  title="Close menu"
                  onClick={() => setOpen(null)}
                >
                  <X size={19} />
                </button>
              </div>
              <div className="mega-columns">
                {open === "downloads" ? (
                  <>
                    <div>
                      <h3>WordPress Assets</h3>
                      <a href="/downloads/wordpress/">
                        WordPress Plugins & Themes (110+)
                        <span>Speed, SEO, Security, WooCommerce & Elementor addons</span>
                      </a>
                      <a href="/downloads/wordpress/">
                        Browse All WordPress Downloads
                        <span>100% Free with Gmail sign-in</span>
                      </a>
                    </div>
                    <div>
                      <h3>Landing Pages & Templates</h3>
                      <a href="/downloads/landing-pages/">
                        Landing Page Templates (110+)
                        <span>Tailwind CSS, HTML5, Next.js & Elementor kits</span>
                      </a>
                      <a href="/downloads/scripts/">
                        Automation & AI Scripts (110+)
                        <span>Python, Node bots, webhooks & crawlers</span>
                      </a>
                    </div>
                    <div>
                      <h3>Tools & Business Systems</h3>
                      <a href="/downloads/tools/">
                        Web Tools & Calculators (100+)
                        <span>Embeddable JS calculators & proposal builders</span>
                      </a>
                      <a href="/downloads/marketing/">
                        Marketing & SOP Systems (95+)
                        <span>Cold email swipe files, Notion templates & pitch decks</span>
                      </a>
                      <a href="/downloads/" style={{ fontWeight: "bold", color: "var(--accent)" }}>
                        Open Complete 500+ Assets Vault →
                      </a>
                    </div>
                  </>
                ) : open === "shop" ? (
                  <>
                    <div>
                      <h3>Affiliate Shop</h3>
                      <a href="/shop/">
                        All recommendations
                        <span>Verified product research with source links</span>
                      </a>
                      <a href="/shop/?category=digitization">
                        Digitization
                        <span>Scanners and paper-to-workflow gear</span>
                      </a>
                    </div>
                    <div>
                      <h3>Workflows</h3>
                      <a href="/shop/?category=content">
                        Content creation
                        <span>Audio, video and production desk tools</span>
                      </a>
                      <a href="/shop/?category=productivity">
                        Productivity
                        <span>Desk tools for daily work</span>
                      </a>
                    </div>
                    <div>
                      <h3>Before You Buy</h3>
                      <a href="/shop/?category=mobility">
                        Mobile work
                        <span>Portable power and storage</span>
                      </a>
                      <a href="/shop/#affiliate-disclosure">
                        Affiliate disclosure
                        <span>How BizAIPro may earn from qualifying purchases</span>
                      </a>
                    </div>
                  </>
                ) : open === "resources" ? (
                  <>
                    <div>
                      <h3>Learn & Explore</h3>
                      <a href="/markets/">
                        Market Intelligence
                        <span>Live Bitcoin, crypto, stock and commodity dashboard</span>
                      </a>
                      <a href="/blog/">
                        Business guides
                        <span>Authored articles and practical workflows</span>
                      </a>
                      <a href="/ai-tools/">
                        AI tools guide
                        <span>Explore the published AI resources</span>
                      </a>
                    </div>
                    <div>
                      <h3>Build & Improve</h3>
                      <a href="/tools/business-studio/">
                        Business Studio
                        <span>Free local invoices, quotations and receipts</span>
                      </a>
                      <a href="/business-automation/">
                        Business automation
                        <span>Plan and review your next workflow</span>
                      </a>
                      <a href="/free-tools/">
                        Free tools
                        <span>Calculators, generators and public checks</span>
                      </a>
                    </div>
                    <div>
                      <h3>BizAIPro</h3>
                      {settings.footerLinks.map((l) => (
                        <a key={l.url} href={l.url}>
                          {l.label}
                        </a>
                      ))}
                    </div>
                  </>
                ) : (
                  groups.map((category) => {
                    const list = tools
                      .filter((t) => t.category === category)
                      .slice(0, 3);
                    return (
                      <div key={category}>
                        <h3>
                          <a href={"/tools/" + category + "/"}>
                            {categories.find((c) => c[0] === category)?.[1]}
                            <ArrowUpRight size={13} />
                          </a>
                        </h3>
                        {list.map((t) => (
                          <a key={t.slug} href={t.path}>
                            {t.name}
                            <span>{t.description}</span>
                          </a>
                        ))}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            <aside className="mega-feature">
              <img
                src="/brand/bizaipro-icon.png"
                alt="BizAIPro Platform Icon"
                width="64"
                height="64"
              />
              <h3>Start with your website</h3>
              <p>Check public page configuration and review the next steps.</p>
              <a className="text-link" href="/tools/website-health-checker/">
                Website Health Checker
                <ArrowRight size={17} />
              </a>
              <a href="/free-tools/">
                All free tools
                <ArrowUpRight size={15} />
              </a>
            </aside>
          </div>
        </div>
      )}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onAuthenticated={(m) => setMember(m)}
      />
    </header>
  );
}
function ToolCard({ tool, favorite, onFavorite }) {
  const Icon = icons[tool.category] || Layers;
  const details = getToolDetails(tool);
  const categoryLabel = categories.find((c) => c[0] === tool.category)?.[1] || tool.category;
  return (
    <article className={"platform-tool-card category-card-" + tool.category}>
      <div className="tool-card-top">
        <span className={"tool-category-badge category-" + tool.category}>
          <Icon size={16} />
          <span className="cat-label">{categoryLabel}</span>
        </span>
        <div className="tool-card-meta-top">
          <span className="rating-pill">Free tool</span>
          {onFavorite && (
            <button
              type="button"
              title={favorite ? "Remove favorite" : "Save favorite"}
              aria-label={(favorite ? "Remove favorite: " : "Save favorite: ") + tool.name}
              aria-pressed={favorite}
              onClick={(e) => { e.preventDefault(); onFavorite(tool.slug); }}
              className="favorite-toggle"
            >
              <Star size={16} fill={favorite ? "currentColor" : "none"} />
            </button>
          )}
        </div>
      </div>
      <div className="tool-card-body">
        <h3>
          <a href={tool.path}>{tool.name}</a>
        </h3>
        <p>{tool.description}</p>
        <div className="tool-card-highlights">
          <span className="tool-badge-pill">
            <Sparkles size={12} /> {details.badge || 'Live Business Utility'}
          </span>
        </div>
      </div>
      <div className="tool-card-bottom-bar">
        <a className="button small tool-launch-btn" href={tool.path}>
          <span>Open Tool</span>
          <ArrowRight size={14} />
        </a>
        <span className="tool-user-count">No signup required</span>
      </div>
    </article>
  );
}
function AffiliateDisclosure() {
  return (
    <div className="affiliate-disclosure" id="affiliate-disclosure">
      <strong>Amazon affiliate disclosure:</strong> As an Amazon Associate I earn from qualifying purchases. Product availability, exact variants and prices can change on Amazon; verify the final listing before buying.
    </div>
  );
}
function ProductCard({ product }) {
  return (
    <article className="shop-card">
      <div className="shop-card-top">
        <span className="tool-category-icon category-business">
          <PackageSearch size={22} />
        </span>
        <span>{shopCategoryLabel(product.category)}</span>
      </div>
      <h3><a href={`/shop/${product.slug}/`}>{product.name}</a></h3>
      <p>{product.summary}</p>
      <div className="shop-meta"><span>{product.brand}</span><span>{product.model}</span></div>
      <div className="shop-actions">
        <a className="text-link" href={`/shop/${product.slug}/`}>Buying guide <ArrowRight size={16} /></a>
        <a className="button light small" href={shopSearchLink(product)} target="_blank" rel="sponsored noopener noreferrer">Find on Amazon <ArrowUpRight size={15} /></a>
      </div>
    </article>
  );
}
export function ShopPage({ data }) {
  const query = data.query || "";
  const category = data.category || "";
  const categoryParam = category ? "&category=" + encodeURIComponent(category) : "";
  const queryParam = query ? "&q=" + encodeURIComponent(query) : "";
  return (
    <section className="container platform-section shop-page">
      <nav className="breadcrumbs"><a href="/">Home</a><span>/</span><span>Shop</span></nav>
      <div className="shop-hero">
        <div>
          <span className="eyebrow">BIZAIPRO AFFILIATE SHOP</span>
          <h1>Useful gear for small business workflows</h1>
          <p>Equipment for paper-to-digital workflows, a more productive desk, clearer client calls and work on the move.</p>
        </div>
      </div>
      <AffiliateDisclosure />
      <form className="directory-controls" action="/shop/">
        <label className="search-form"><Search size={20} /><input name="q" defaultValue={query} aria-label="Search shop products" placeholder="Search scanners, microphones, webcams..." /></label>
        <label>Category<select name="category" defaultValue={category}><option value="">All categories</option>{shopCategories.map(([slug,label])=><option key={slug} value={slug}>{label}</option>)}</select></label>
        <button className="button small">Search</button>
      </form>
      <nav className="category-tabs" aria-label="Shop categories">
        <a href="/shop/" aria-current={!category ? "page" : undefined}>All products</a>
        {shopCategories.map(([slug,label])=><a key={slug} href={`/shop/?category=${slug}`} aria-current={category===slug?"page":undefined}>{label}</a>)}
      </nav>
      <p className="results-count" role="status">{data.total || 0} products</p>
      <div className="shop-grid">{(data.products || []).map(product => <ProductCard key={product.slug} product={product} />)}</div>
      {!data.products?.length && <div className="empty-state"><h2>No matching products</h2><p>Try another search or clear the category filter.</p><a className="button light" href="/shop/">Clear filters</a></div>}
      <div className="pagination">
        {data.page > 1 && <a className="button light" href={`/shop/?page=${data.page-1}${queryParam}${categoryParam}`}>Previous</a>}
        {data.total > data.page * 12 && <a className="button" href={`/shop/?page=${data.page+1}${queryParam}${categoryParam}`}>Next page</a>}
      </div>
    </section>
  );
}
export function ShopProductPage({ data }) {
  const product = data.product;
  return (
    <section className="container platform-section shop-product-page">
      <nav className="breadcrumbs"><a href="/">Home</a><span>/</span><a href="/shop/">Shop</a><span>/</span><span>{product.name}</span></nav>
      <AffiliateDisclosure />
      <header className="shop-product-hero">
        <div>
          <span className="eyebrow">{shopCategoryLabel(product.category)} / {product.brand}</span>
          <h1>{product.guideTitle}</h1>
          <p>{product.summary}</p>
          <div className="shop-actions">
            <a className="button" href={shopSearchLink(product)} target="_blank" rel="sponsored noopener noreferrer">Find on Amazon <ArrowUpRight size={16} /></a>
            <a className="button light" href={product.sourceUrl} target="_blank" rel="noopener noreferrer">Manufacturer source <ArrowUpRight size={16} /></a>
          </div>
          <small>Opens Amazon search results. Check the exact model, seller, price and availability before purchasing.</small>
        </div>
        <div className="product-source-box">
          <PackageSearch size={38} />
          <h2>{product.name}</h2>
          <p>{product.brand} {product.model}</p>
        </div>
      </header>
      <div className="buying-guide-grid">
        <article>
          <h2>Best fit</h2>
          <p>{product.workflowFit}</p>
          <h2>Good for</h2>
          <ul>{product.bestFor.map(item => <li key={item}>{item}</li>)}</ul>
        </article>
        <article>
          <h2>Caveats</h2>
          <ul>{product.caveats.map(item => <li key={item}>{item}</li>)}</ul>
          <h2>Manufacturer notes</h2>
          <ul>{product.manufacturerNotes.map(item => <li key={item}>{item}</li>)}</ul>
        </article>
      </div>
      <section className="tool-disclosures">
        <h2>Source and buying notes</h2>
        <p>Based on manufacturer documentation, not hands-on testing. Equipment supports your workflow; it does not replace AI software or guarantee business results.</p>
        <p><a className="text-link" href={product.sourceUrl} target="_blank" rel="noopener noreferrer">{product.sourceLabel}<ArrowUpRight size={16} /></a></p>
      </section>
      {data.related?.length > 0 && <section className="related-tools"><div className="section-heading"><h2>Related shop guides</h2><a className="text-link" href="/shop/">All products <ArrowRight size={17} /></a></div><div className="shop-grid three">{data.related.map(p => <ProductCard key={p.slug} product={p} />)}</div></section>}
    </section>
  );
}
export function PlatformSearch({ data }) {
  const matches = tools.filter((t) =>
    (t.name + " " + t.description + " " + t.keywords.join(" "))
      .toLowerCase()
      .includes(data.query.toLowerCase()),
  );
  return (
    <section className="container platform-section">
      <div className="directory-heading">
        <span className="eyebrow">BIZAIPRO SEARCH</span>
        <h1>Results for &quot;{data.query}&quot;</h1>
        <form className="search-form" action="/search/">
          <Search size={20} />
          <input
            name="q"
            aria-label="Search tools and guides"
            defaultValue={data.query}
          />
          <button className="button small">Search</button>
        </form>
      </div>
      <h2>Tools</h2>
      <div className="platform-tool-grid">
        {matches.map((tool) => (
          <ToolCard key={tool.slug} tool={tool} />
        ))}
      </div>
      {!matches.length && <p>No matching tools.</p>}
      <section className="related-tools">
        <h2>Guides</h2>
        <div className="platform-guide-grid">
          {data.posts.map((p) => (
            <article key={p._id}>
              <h3>
                <a href={"/" + p.slug + "/"}>{p.title}</a>
              </h3>
              <p>{p.excerpt}</p>
              <a className="text-link" href={"/" + p.slug + "/"}>
                Read guide
                <ArrowRight size={16} />
              </a>
            </article>
          ))}
        </div>
        {!data.posts.length && <p>No matching guides.</p>}
      </section>
      {data.total > data.page * 12 && (
        <a
          className="button light"
          href={
            "?q=" + encodeURIComponent(data.query) + "&page=" + (data.page + 1)
          }
        >
          More guides
        </a>
      )}
    </section>
  );
}
export function PlatformHome({ data }) {
  return (
    <>
      <section className="platform-hero">
        <div className="container future-hero-layout">
          <div className="future-hero-copy">
          <span className="eyebrow">BIZAIPRO 2.0 · AI TOOLS, DIGITAL PRODUCTS &amp; CUSTOM SOFTWARE</span>
          <h1>AI Tools, Digital Products &amp; <span>Custom Tech Solutions</span></h1>
          <p>
            Power your business with 87+ zero-signup calculators and tools, 525+ free digital vault assets, production-ready software packages, or get custom software engineered for your team.
          </p>
          <div className="hero-buttons">
            <a className="button" href="/free-tools/">
              Explore Free Tools (87+)
              <ArrowRight size={18} />
            </a>
            <a className="button light" href="/marketplace/">
              Digital Marketplace
              <PackageSearch size={18} />
            </a>
            <a className="button light" href="#custom-solutions">
              Custom Engineering
              <Code size={18} />
            </a>
          </div>
          <div className="hero-assurances">
            <span>
              <Check size={16} />
              87+ In-Browser Tools
            </span>
            <span>
              <Check size={16} />
              525+ Free Assets Vault
            </span>
            <span>
              <Check size={16} />
              Zero Paid Subscriptions
            </span>
            <span>
              <Check size={16} />
              Google Sign-In Available
            </span>
          </div>
          </div>
          <HomeWorkspace/>
        </div>
      </section>

      {/* 4 Core Pillars Section */}
      <section className="container bza-pillars-section">
        <div className="bza-pillars-grid">
          <a href="/free-tools/" className="bza-pillar-card">
            <div className="bza-pillar-icon">
              <Calculator size={22} />
            </div>
            <span className="bza-badge bza-badge-brand bza-pillar-badge">87+ Tools</span>
            <h2>Free Web &amp; Business Tools</h2>
            <p>Calculate ROAS, audit website speed, generate invoices, inspect SEO, and automate daily tasks without signing up.</p>
            <span className="bza-pillar-cta">Open toolbox <ArrowRight size={14} /></span>
          </a>

          <a href="/marketplace/" className="bza-pillar-card">
            <div className="bza-pillar-icon">
              <PackageSearch size={22} />
            </div>
            <span className="bza-badge bza-badge-success bza-pillar-badge">Commercial</span>
            <h2>Digital Products Marketplace</h2>
            <p>Turnkey MERN web applications, automated SEO scrapers, and standalone billing software with lifetime commercial licenses.</p>
            <span className="bza-pillar-cta">Browse products <ArrowRight size={14} /></span>
          </a>

          <a href="/downloads/" className="bza-pillar-card">
            <div className="bza-pillar-icon">
              <Sparkles size={22} />
            </div>
            <span className="bza-badge bza-badge-brand bza-pillar-badge">500+ Assets</span>
            <h2>Curated Free Assets Vault</h2>
            <p>WordPress plugins, responsive landing page templates, Python automation scripts, and SOP blueprints unlocked with Gmail.</p>
            <span className="bza-pillar-cta">Explore vault <ArrowRight size={14} /></span>
          </a>

          <a href="#custom-solutions" className="bza-pillar-card">
            <div className="bza-pillar-icon">
              <Code size={22} />
            </div>
            <span className="bza-badge bza-badge-warning bza-pillar-badge">Custom Dev</span>
            <h2>Custom Tech Engineering</h2>
            <p>Need bespoke software? We build high-performance MERN web apps, API integrations, and custom workflow automations.</p>
            <span className="bza-pillar-cta">Get an estimate <ArrowRight size={14} /></span>
          </a>
        </div>
      </section>
      <section className="checker-band">
        <div className="container">
          <div className="checker-heading">
            <div>
              <span className="eyebrow">KNOW WHAT TO IMPROVE</span>
              <h2>How healthy is your website?</h2>
              <p>
                Review public metadata, HTTPS, headers and real Lighthouse
                results.
              </p>
            </div>
            <a href="/tools/website-health-checker/" className="text-link">
              Open full checker
              <ArrowUpRight size={17} />
            </a>
          </div>
          <NetworkTool compact />
        </div>
      </section>
      <section className="container platform-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">GET STRAIGHT TO WORK</span>
            <h2>Popular tools</h2>
          </div>
          <a className="text-link" href="/free-tools/">
            All tools
            <ArrowRight size={17} />
          </a>
        </div>
        <div className="platform-tool-grid">
          {featuredSlugs
            .map((slug) => tools.find((t) => t.slug === slug))
            .filter(Boolean)
            .map((tool) => (
              <ToolCard tool={tool} key={tool.slug} />
            ))}
        </div>
      </section>
      <section className="outcomes-band">
        <div className="container outcomes-grid">
          {[
            [
              Globe,
              "Improve your website",
              "Find configuration issues and review your next fixes.",
              "/tools/website/",
            ],
            [
              ChartNoAxesCombined,
              "Plan your marketing",
              "Build campaign links and calculate your returns.",
              "/tools/marketing/",
            ],
            [
              Calculator,
              "Understand your numbers",
              "Use your own assumptions to compare business decisions.",
              "/tools/calculators/",
            ],
            [
              Workflow,
              "Reduce repetitive work",
              "Estimate automation savings and explore workflows.",
              "/tools/ai/",
            ],
          ].map(([Icon, title, description, path]) => (
            <a key={path} href={path}>
              <Icon size={25} />
              <h2>{title}</h2>
              <p>{description}</p>
              <ArrowUpRight size={18} />
            </a>
          ))}
        </div>
      </section>
      {[
        [
          "website",
          "A better website starts with a clearer picture.",
          "Public website checks and practical SEO utilities.",
        ],
        [
          "marketing",
          "Make your next campaign measurable.",
          "Campaign links, QR codes and marketing utilities.",
        ],
        [
          "calculators",
          "Decisions backed by your numbers.",
          "Business calculators without a subscription.",
        ],
        [
          "ai",
          "Put automation in perspective.",
          "Estimate value before committing to an AI workflow.",
        ],
      ].map(([category, title, description]) => (
        <section
          className="container platform-section category-section"
          key={category}
        >
          <div className="section-heading">
            <div>
              <span className="eyebrow">
                {categories.find((c) => c[0] === category)?.[1]}
              </span>
              <h2>{title}</h2>
              <p>{description}</p>
            </div>
            <a className="text-link" href={"/tools/" + category + "/"}>
              Explore category
              <ArrowRight size={17} />
            </a>
          </div>
          <div className="platform-tool-grid three">
            {tools
              .filter((t) => t.category === category)
              .slice(0, 3)
              .map((tool) => (
                <ToolCard key={tool.slug} tool={tool} />
              ))}
          </div>
        </section>
      ))}
      <section className="recent-band">
        <div className="container platform-section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">LATEST TOOL ADDITIONS</span>
              <h2>More ways to move forward</h2>
            </div>
          </div>
          <div className="platform-tool-grid three">
            {[
              "business-studio",
              "invoice-generator",
              "automation-savings-calculator",
            ]
              .map((s) => tools.find((t) => t.slug === s))
              .map((tool) => (
                <ToolCard key={tool.slug} tool={tool} />
              ))}
          </div>
        </div>
      </section>
      <section className="container platform-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">PRACTICAL KNOWLEDGE</span>
            <h2>Guides for your next step</h2>
          </div>
          <a className="text-link" href="/blog/">
            All guides
            <ArrowRight size={17} />
          </a>
        </div>
        <div className="platform-guide-grid">
          {(data.posts || []).slice(0, 3).map((p) => (
            <article key={p._id}>
              {p.featuredImage && (
                <a href={"/" + p.slug + "/"} tabIndex={-1} aria-hidden="true">
                  <img src={p.featuredImage} alt={p.imageAlt||p.title} loading="lazy" width="800" height="450" />
                </a>
              )}
              <span className="eyebrow">{p.category || "Guides"}</span>
              <h3>
                <a href={"/" + p.slug + "/"}>{p.title}</a>
              </h3>
              <p>{p.excerpt}</p>
              <a className="text-link" href={"/" + p.slug + "/"}>
                Read guide
                <ArrowUpRight size={16} />
              </a>
            </article>
          ))}
        </div>
      </section>

      {/* Custom Solutions & Interactive Lead Qualification Section */}
      <section id="custom-solutions" className="bza-consultation-section">
        <div className="container">
          <div className="bza-consultation-grid">
            <div className="bza-consultation-copy">
              <span className="eyebrow">CUSTOM ENGINEERING &amp; SAAS ARCHITECTURE</span>
              <h2>Need Custom Tech Built for Your Business?</h2>
              <p>
                From custom full-stack MERN platforms to automated lead capture pipelines and high-converting web applications, our engineering team designs and delivers production-grade software tailored to your exact business workflow.
              </p>

              <div className="bza-feature-checklist">
                <div className="bza-feature-item">
                  <span className="bza-check-icon"><Check size={14} /></span>
                  <div className="bza-feature-text">
                    <strong>Full-Stack MERN Architecture</strong>
                    <span>Engineered with Node.js, Express, React, and MongoDB for extreme speed and scalability.</span>
                  </div>
                </div>

                <div className="bza-feature-item">
                  <span className="bza-check-icon"><Check size={14} /></span>
                  <div className="bza-feature-text">
                    <strong>100% Full Source Code Ownership</strong>
                    <span>Zero ongoing licensing royalties or vendor lock-in. You own and control every line of code.</span>
                  </div>
                </div>

                <div className="bza-feature-item">
                  <span className="bza-check-icon"><Check size={14} /></span>
                  <div className="bza-feature-text">
                    <strong>Google Identity &amp; Built-In CRM Pipelines</strong>
                    <span>Seamless Google authentication, lead capture forms, and administrative management portals.</span>
                  </div>
                </div>

                <div className="bza-feature-item">
                  <span className="bza-check-icon"><Check size={14} /></span>
                  <div className="bza-feature-text">
                    <strong>Dedicated 30-Day Post-Launch Support</strong>
                    <span>Full deployment on VPS, Docker, or Hostinger with complete documentation and guarantee.</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bza-consultation-card">
              <h3>Request Project Brief &amp; Estimate</h3>
              <p>Tell us about your project requirements to receive a detailed technical breakdown and timeline estimate.</p>
              <ConsultationForm />
            </div>
          </div>
        </div>
      </section>

      <PublicSearchContent slug="home" home/>
      <section className="why-band">
        <div className="container">
          <img src="/brand/bizaipro-icon.png" width="72" height="72" alt="BizAIPro Business Tools Icon" />
          <div>
            <h2>Useful work. Clear expectations.</h2>
            <p>
              Browser tools keep inputs local. Public website checks disclose
              the providers they use. Estimates are based on the numbers you
              supply, and missing data stays missing.
            </p>
            <a className="text-link" href="/free-tools/">
              Find your next tool
              <ArrowRight size={17} />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
export function PlatformDirectory({ category, query = "" }) {
  const [q, setQ] = useState(query),
    [sort, setSort] = useState("recommended"),
    [favorites, setFavorites] = useState([]),
    [onlyFavorites, setOnlyFavorites] = useState(false);
  useEffect(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem("bizaipro-favorites") || "[]",
      );
      if (Array.isArray(saved))
        setFavorites(saved.filter((s) => tools.some((t) => t.slug === s)));
    } catch {}
  }, []);
  const toggle = (slug) => {
    const next = favorites.includes(slug)
      ? favorites.filter((s) => s !== slug)
      : [...favorites, slug];
    setFavorites(next);
    try {
      localStorage.setItem("bizaipro-favorites", JSON.stringify(next));
    } catch {}
  };
  const list = tools
    .filter(
      (t) =>
        (!category || t.category === category) &&
        (!onlyFavorites || favorites.includes(t.slug)) &&
        (t.name + " " + t.description + " " + t.keywords.join(" "))
          .toLowerCase()
          .includes(q.toLowerCase()),
    )
    .sort((a, b) =>
      sort === "name" ? a.name.localeCompare(b.name) : a.order - b.order,
    );
  return (
    <section className="container platform-section directory-page">
      <nav className="breadcrumbs">
        <a href="/">Home</a>
        <span>/</span>
        <a href="/free-tools/">Free Tools</a>
        {category && (
          <>
            <span>/</span>
            <span>{categories.find((c) => c[0] === category)?.[1]}</span>
          </>
        )}
      </nav>
      <div className="directory-heading">
        <span className="eyebrow">THE BIZAIPRO TOOLBOX</span>
        <h1>
          {category
            ? categories.find((c) => c[0] === category)?.[1] + " Tools"
            : "Free tools for better business decisions"}
        </h1>
        <p>Calculators, generators and public checks. No signup required.</p>
      </div>
      <div className="directory-controls">
        <label className="search-form">
          <Search size={20} />
          <input
            aria-label="Search tools"
            placeholder="Search tools..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <label>
          Sort
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="recommended">Recommended</option>
            <option value="name">Name A-Z</option>
          </select>
        </label>
        <label className="check-label">
          <input
            type="checkbox"
            checked={onlyFavorites}
            onChange={(e) => setOnlyFavorites(e.target.checked)}
          />
          Favorites
        </label>
      </div>
      <nav className="category-tabs" aria-label="Tool categories">
        <a href="/free-tools/" aria-current={!category ? "page" : undefined}>
          All tools
        </a>
        {categories.map(([slug, label]) => (
          <a
            key={slug}
            href={"/tools/" + slug + "/"}
            aria-current={category === slug ? "page" : undefined}
          >
            {label}
          </a>
        ))}
      </nav>
      <p className="results-count" role="status">
        {list.length} tools
      </p>
      <div className="platform-tool-grid">
        {list.map((tool) => (
          <ToolCard
            key={tool.slug}
            tool={tool}
            favorite={favorites.includes(tool.slug)}
            onFavorite={toggle}
          />
        ))}
      </div>
      {!list.length && (
        <div className="empty-state">
          <h2>No matching tools</h2>
          <p>Try another search or clear the favorites filter.</p>
          <button
            className="button light"
            onClick={() => {
              setQ("");
              setOnlyFavorites(false);
            }}
          >
            Clear filters
          </button>
        </div>
      )}
    </section>
  );
}
export function PlatformToolPage({ data }) {
  const tool = data.tool;
  const isSeoAudit = tool.slug === 'website-seo-score-checker';
  const guide = searchToolGuides[tool.slug] || toolGuides[tool.slug];
  const details = getToolDetails(tool);

  useEffect(() => {
    try {
      const current = JSON.parse(
        localStorage.getItem("bizaipro-recent") || "[]",
      );
      localStorage.setItem(
        "bizaipro-recent",
        JSON.stringify(
          [
            tool.slug,
            ...(Array.isArray(current) ? current : []).filter(
              (s) => s !== tool.slug,
            ),
          ].slice(0, 8),
        ),
      );
    } catch {}
  }, [tool.slug]);

  const related = relatedTools(tool);

  return (
    <div className="container platform-section tool-page">
      <nav className="breadcrumbs">
        <a href="/">Home</a>
        <span>/</span>
        <a href="/free-tools/">Free Tools</a>
        <span>/</span>
        <a href={"/tools/" + tool.category + "/"}>
          {categories.find((c) => c[0] === tool.category)?.[1]}
        </a>
        <span>/</span>
        <span aria-current="page">{tool.name}</span>
      </nav>

      {/* Main Single Landing Page Header */}
      <header className="tool-page-heading">
        <div className="tool-header-badges">
          <span className="eyebrow">BIZAIPRO / FREE TOOL</span>
          <span className="tool-detail-badge">{details.badge}</span>
          <span className="tool-rating-pill">
            <CheckCircle2 size={13} color="#10b981" /> 100% Free · No Signup Required
          </span>
        </div>
        <h1>{tool.name}</h1>
        <p className="tool-page-deck">{tool.description}</p>
        <div className="tool-header-meta">
          <span className="processing-note">
            <Shield size={15} />
            {tool.processing}
          </span>
          <span className="verified-date">
            <CheckCircle2 size={15} color="#10b981" /> {isSeoAudit ? 'Single-page HTML checks' : 'Browser-based workspace'}
          </span>
        </div>
      </header>

      {/* Active Functional Tool Workspace — POSITIONED FIRST AS REQUESTED */}
      <div className="tool-workspace-card" id="workspace">
        <div className="workspace-header">
          <div className="workspace-title-box">
            <span className="live-status"><span className="live-pulse"></span> Live Interactive Tool</span>
            <h2>{tool.name} Workspace</h2>
          </div>
          <div className="workspace-badge-row">
            <span className="tool-instant-chip">{tool.engine === "network" ? "Public URL analysis" : "⚡ Instant Engine"}</span>
            <span className="tool-privacy-chip">{tool.engine === "network" ? "Public URL sent for analysis" : "🔒 100% Private (Runs In Your Browser)"}</span>
          </div>
        </div>
        <div className="workspace-inner-content">
          {tool.engine === "network" ? (
            <NetworkTool
              kind={
                {
                  "website-health-checker": "website",
                  "website-seo-score-checker": "website",
                  "website-speed-test": "speed",
                  "dns-checker": "dns",
                  "domain-age-checker": "domain",
                }[tool.slug]
              }
            />
          ) : tool.engine === "legacy" &&
            ![
              "utm-builder",
              "schema-markup-generator",
              "search-snippet-preview",
            ].includes(tool.slug) ? (
            <ToolWorkspace slug={tool.slug} />
          ) : (
            <Suspense
              fallback={
                <p className="loading" role="status">
                  Loading tool...
                </p>
              }
            >
              <Extra slug={tool.slug} />
            </Suspense>
          )}
        </div>
      </div>

      {/* Hero Visual Mockup & Architecture Card */}
      {isSeoAudit && guide && <SeoAuditGuide guide={guide} />}
      {!isSeoAudit && <div className="tool-hero-mockup-wrapper">
        <ToolVisualCard tool={tool} details={details} size="large" />
      </div>}

      {/* Pros & Cons Section */}
      <section className="tool-pros-cons-section">
        <div className="section-title-wrap">
          <span className="eyebrow">OBJECTIVE ASSESSMENT</span>
          <h2>Pros & Cons of {tool.name}</h2>
          <p>An honest evaluation of strengths and practical operational considerations.</p>
        </div>
        <div className="pros-cons-grid">
          <div className="pros-card">
            <div className="pc-card-header green">
              <ThumbsUp size={20} />
              <h3>Key Advantages & Pros</h3>
            </div>
            <ul className="pc-list">
              {details.pros.map((pro, i) => (
                <li key={i}>
                  <CheckCircle2 size={16} className="pc-icon green" />
                  <span>{pro}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="cons-card">
            <div className="pc-card-header amber">
              <AlertCircle size={20} />
              <h3>Considerations & Caveats</h3>
            </div>
            <ul className="pc-list">
              {details.cons.map((con, i) => (
                <li key={i}>
                  <AlertCircle size={16} className="pc-icon amber" />
                  <span>{con}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Step-by-Step How to Use Guide */}
      <section className="tool-how-to-use-section">
        <div className="section-title-wrap">
          <span className="eyebrow">STEP-BY-STEP WALKTHROUGH</span>
          <h2>How to Use {tool.name} Effectively</h2>
          <p>Follow these 4 simple steps to maximize accuracy and integrate results into your workflow.</p>
        </div>
        <div className="how-to-steps-grid">
          {details.howToUse.map((step) => (
            <div key={step.step} className="step-card">
              <div className="step-badge">0{step.step}</div>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Real-World Use Cases */}
      <section className="tool-use-cases-section">
        <h2>Recommended Business Use Cases</h2>
        <div className="use-cases-grid">
          {details.useCases.map((uc, i) => (
            <div key={i} className="use-case-card">
              <Sparkles size={20} className="uc-icon" />
              <p>{uc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      {details.faqs?.length > 0 && (
        <section className="tool-faqs-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faqs-grid">
            {details.faqs.map((faq, i) => (
              <div key={i} className="faq-item">
                <h3><HelpCircle size={18} /> {faq.q}</h3>
                <p>{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {guide && !isSeoAudit && (
        <section className="tool-disclosures">
          <h2>{guide.heading}</h2>
          <p><strong>{guide.methodLabel || "Formula"}:</strong> {guide.formula}</p>
          <h3>Worked Example</h3>
          <p>{guide.example}</p>
          <h3>{guide.limitsHeading || "Limits of This Calculation"}</h3>
          <p>{guide.limitation}</p>
          {guide.sections?.map(section=><div key={section.heading}><h3>{section.heading}</h3><p>{section.text}</p></div>)}
          {guide.links?.length>0&&<nav aria-label={guide.methodLabel ? "Next steps for your page" : "Continue your calculation"}><h3>{guide.methodLabel ? "Next steps for your page" : "Continue your calculation"}</h3><ul>{guide.links.map(link=><li key={link.href}><a href={link.href}>{link.label}</a></li>)}</ul></nav>}
        </section>
      )}

      {data.post?.content && (
        <section
          className="tool-authored-content prose"
          dangerouslySetInnerHTML={{ __html: data.post.content }}
        />
      )}

      <section className="tool-disclosures">
        <h2>About this tool & Privacy Guarantee</h2>
        <p>
          {tool.engine === "network"
            ? "Public checks depend on the website and provider availability. Results are snapshots, not guarantees of ranking, security or performance. Cached reports are marked when reused."
            : tool.engine === "password"
              ? "A match refers to the known Pwned Passwords dataset. A missing match does not prove a password is safe."
              : "Results use the inputs you provide. Review the output before using it in a business decision or publishing it."}
        </p>
        <h3>Do I need an account?</h3>
        <p>No. This tool is 100% free and available without registration.</p>
        <h3>Where are my inputs processed?</h3>
        <p>
          {tool.processing}.{" "}
          {tool.engine === "network"
            ? "Never submit credentials or private/internal URLs."
            : tool.engine === "password"
              ? "The raw password and full hash never leave your browser."
              : "Inputs are not uploaded or logged by this tool."}
        </p>
      </section>

      {related.length > 0 && (
        <section className="related-tools">
          <div className="section-heading">
            <h2>Related {categories.find((c) => c[0] === tool.category)?.[1]} Tools</h2>
            <a className="text-link" href={"/tools/" + tool.category + "/"}>
              All {categories.find((c) => c[0] === tool.category)?.[1]} tools{" "}
              <ArrowRight size={17} />
            </a>
          </div>
          <div className="platform-tool-grid three">
            {related.map((t) => (
              <ToolCard key={t.slug} tool={t} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
